/**
 * bridge/photo_storage_controller.js — "photo-storage" bridge component
 * (Stimulus + Strada).
 *
 * Byte storage for offline-captured photos. Stimulus identifier
 * "bridge--photo-storage" (glob-derived from this subdir); Strada component
 * name "photo-storage" MUST match the iOS class var name
 * (native/ios/Chippie/Bridge/PhotoStorageComponent.swift).
 *
 * Why it exists: navigator.storage.persist() is DENIED in both native
 * WebViews (measured — chippie-kc0hi.11 iPhone 12/iOS 18.7, chippie-ra7j4.1
 * Pixel 5/Android 14), and web storage is one evictable origin bucket. An
 * offline-captured photo is the only in-scope item whose local copy is the
 * SOLE copy of user data, so its bytes go in the NATIVE app container on the
 * shells (survives a webview-storage wipe) and OPFS on the plain PWA — the
 * weaker PWA durability is a stated property, not a bug: an evicted origin
 * loses unsynced captures along with the pending metadata rows that point at
 * them.
 *
 * API — window.ChippieBridge.photoStorage (Promise-based; bytes cross the
 * native bridge base64-encoded, chunk-free — photos are a few MB, measure
 * before optimising):
 *   .save(key, blob) -> Promise<Boolean>
 *   .read(key)       -> Promise<Blob | null>   null when the key is absent
 *   .delete(key)     -> Promise<Boolean>       absent key counts as success
 *
 * The blob's mime type must survive the round trip (the flush POST's multipart
 * content-type is validated server-side), so the native payload is the
 * envelope "mime\nbase64" and the OPFS impl writes a sidecar <name>.mime file.
 *
 * Three-way transport (facade SSOT, chippie-bridge.js owns platform detection):
 *   - ios:     this.send("save"|"read"|"delete", { key, data?, mime? }) ->
 *              files under Application Support/ChippiePhotos (backed up on
 *              purpose: sole-copy user data should ride device backups).
 *   - android: window.ChippieNative.photoSave/photoRead/photoDelete ->
 *              files under the app's filesDir/photos.
 *   - pwa:     OPFS via navigator.storage.getDirectory() under photos/.
 *
 * Keys are issued by the capture seam (gallery_controller.js) — this adapter
 * never invents keys; each impl sanitises them to a safe filename internally.
 *
 * Zero business logic: no validation, no image processing (EXIF/orient stays
 * server-side in AttachmentService).
 *
 * Mounted app-wide in templates/components/bridge_host.html.
 */
import { BridgeComponent } from "@hotwired/hotwire-native-bridge";

function blobToBase64(blob) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result).split(",", 2)[1] || "");
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(blob);
    });
}

function base64ToBlob(base64, mime) {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
    return new Blob([bytes], { type: mime || "application/octet-stream" });
}

/** Split the "mime\nbase64" native envelope back into a Blob. */
function envelopeToBlob(envelope) {
    if (envelope == null) return null;
    const sep = envelope.indexOf("\n");
    if (sep < 0) return base64ToBlob(envelope, "");
    return base64ToBlob(envelope.slice(sep + 1), envelope.slice(0, sep));
}

function androidImpl() {
    const missing = () => Promise.reject(new Error("photo-storage-unavailable"));
    return {
        save: async (key, blob) => {
            const native = window.ChippieNative;
            if (!native || !native.photoSave) return missing();
            const base64 = await blobToBase64(blob);
            return !!native.photoSave(String(key), base64, blob.type || "");
        },
        read: (key) => {
            const native = window.ChippieNative;
            if (!native || !native.photoRead) return missing();
            try {
                const envelope = native.photoRead(String(key));
                return Promise.resolve(envelopeToBlob(envelope === undefined ? null : envelope));
            } catch (e) {
                return Promise.reject(e);
            }
        },
        delete: (key) => {
            const native = window.ChippieNative;
            if (!native || !native.photoDelete) return missing();
            try {
                return Promise.resolve(!!native.photoDelete(String(key)));
            } catch (e) {
                return Promise.reject(e);
            }
        },
    };
}

// ── PWA: OPFS under photos/ ─────────────────────────────────────────────────

async function opfsPhotosDir() {
    const root = await navigator.storage.getDirectory();
    return root.getDirectoryHandle("photos", { create: true });
}

// OPFS entry names cannot contain "/" (keys look like "capture/<uuid>").
function opfsName(key) {
    return encodeURIComponent(String(key));
}

const pwaImpl = {
    save: async (key, blob) => {
        const dir = await opfsPhotosDir();
        const handle = await dir.getFileHandle(opfsName(key), { create: true });
        const writable = await handle.createWritable();
        await writable.write(blob);
        await writable.close();
        // OPFS does not persist a mime type; sidecar keeps the round trip whole.
        const metaHandle = await dir.getFileHandle(opfsName(key) + ".mime", { create: true });
        const metaWritable = await metaHandle.createWritable();
        await metaWritable.write(blob.type || "");
        await metaWritable.close();
        return true;
    },
    read: async (key) => {
        const dir = await opfsPhotosDir();
        let file;
        try {
            file = await (await dir.getFileHandle(opfsName(key))).getFile();
        } catch (e) {
            if (e && e.name === "NotFoundError") return null;
            throw e;
        }
        let mime = "";
        try {
            mime = await (await (await dir.getFileHandle(opfsName(key) + ".mime")).getFile()).text();
        } catch (_e) { /* no sidecar — fall through to a typeless blob */ }
        return mime ? new Blob([file], { type: mime }) : file;
    },
    delete: async (key) => {
        const dir = await opfsPhotosDir();
        for (const name of [opfsName(key), opfsName(key) + ".mime"]) {
            try {
                await dir.removeEntry(name);
            } catch (e) {
                if (!e || e.name !== "NotFoundError") throw e;
            }
        }
        return true;
    },
};

function registerStandalone() {
    if (!window.ChippieBridge || !window.ChippieBridge.register) return;
    window.ChippieBridge.register("photoStorage", {
        // ios is overridden by connect() with real sends; the placeholder
        // rejects so a pre-connect call fails loudly instead of resolving null.
        ios: {
            save: () => Promise.reject(new Error("photo-storage-unavailable")),
            read: () => Promise.reject(new Error("photo-storage-unavailable")),
            delete: () => Promise.reject(new Error("photo-storage-unavailable")),
        },
        android: androidImpl(),
        pwa: pwaImpl,
    });
}

// Facade may load after this module; register as soon as both are present.
if (window.ChippieBridge) {
    registerStandalone();
} else {
    document.addEventListener("DOMContentLoaded", registerStandalone, { once: true });
}

export default class extends BridgeComponent {
    static component = "photo-storage";

    connect() {
        super.connect();
        if (!window.ChippieBridge || !window.ChippieBridge.register) return;
        window.ChippieBridge.register("photoStorage", {
            ios: {
                save: async (key, blob) => {
                    const base64 = await blobToBase64(blob);
                    const data = await this.request("save", {
                        key: String(key), data: base64, mime: blob.type || "",
                    });
                    return !!data.success;
                },
                read: (key) => this.request("read", { key: String(key) })
                    .then((data) => envelopeToBlob(data.value === undefined ? null : data.value)),
                delete: (key) => this.request("delete", { key: String(key) })
                    .then((data) => !!data.success),
            },
            android: androidImpl(),
            pwa: pwaImpl,
        });
    }

    disconnect() {
        registerStandalone();
        super.disconnect();
    }

    // Strada replies through a callback; every method here is Promise-shaped.
    request(event, payload) {
        return new Promise((resolve) => {
            this.send(event, payload, (message) => resolve((message && message.data) || {}));
        });
    }
}
